// src/utils/geopackage-style-utils.ts

import * as L from "leaflet";

/**
 * Types of styles based on geometry type
 */
export enum StyleType {
  Point = "point",
  Line = "line",
  Polygon = "polygon",
  MultiPoint = "multi_point",
  MultiLine = "multi_line",
  MultiPolygon = "multi_polygon",
  Icon = "icon",
}

/**
 * Basic GeoPackage style interface
 */
export interface GeoPackageStyle {
  type?: StyleType;
  color?: string;
  opacity?: number;
  weight?: number;
  radius?: number;
  fillColor?: string;
  fillOpacity?: number;
  icon?: string;
  dashArray?: string;
}

/**
 * Converts QGIS QML-style format (from backend) to GeoPackageStyle format
 */
export function convertQmlStyleToGeoPackageStyle(
  backendStyle: any,
): GeoPackageStyle | null {
  if (!backendStyle) return null;

  // If there's a qml object, extract style from it
  const qml = backendStyle.qml;
  if (!qml) return null;

  const style: GeoPackageStyle = {};

  // Convert RGBA color strings like "193,193,193,179" to hex with opacity
  const parseRgbaColor = (
    rgbaStr: string,
  ): { color: string; opacity: number } | null => {
    if (!rgbaStr) return null;
    const parts = rgbaStr.split(",").map((p) => parseInt(p.trim()));
    if (parts.length < 3) return null;

    const r = parts[0];
    const g = parts[1];
    const b = parts[2];
    const a = parts.length > 3 ? parts[3] / 255 : 1;

    const hex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    return { color: hex, opacity: a };
  };

  // Handle fill color (for polygons)
  if (qml.fill_color) {
    const parsed = parseRgbaColor(qml.fill_color);
    if (parsed) {
      style.fillColor = parsed.color;
      style.fillOpacity = parsed.opacity;
    }
  }

  // Handle outline/stroke color
  if (qml.outline_color) {
    const parsed = parseRgbaColor(qml.outline_color);
    if (parsed) {
      style.color = parsed.color;
      style.opacity = parsed.opacity;
    }
  }

  // Handle custom icon (for RasterMarker points)
  if (qml.icon) {
    style.icon = qml.icon;
  }

  // Handle marker color (for points)
  if (qml.color && !qml.fill_color) {
    const parsed = parseRgbaColor(qml.color);
    if (parsed) {
      style.color = parsed.color;
      style.fillColor = parsed.color;
      style.opacity = parsed.opacity;
      style.fillOpacity = parsed.opacity;
    }
  }

  // Handle line color
  if (qml.line_color || (qml.color && qml.line)) {
    const colorStr = qml.line_color || qml.color;
    const parsed = parseRgbaColor(colorStr);
    if (parsed) {
      style.color = parsed.color;
      style.opacity = parsed.opacity;
    }
  }

  // Handle size/radius
  if (qml.size) {
    style.radius = parseFloat(qml.size);
  }

  // Handle width/weight
  if (qml.width || qml.outline_width) {
    style.weight = parseFloat(qml.width || qml.outline_width);
  }

  // Handle line width
  if (qml.line_width) {
    style.weight = parseFloat(qml.line_width);
  }

  // Determine type based on qml properties
  if (qml.fill) {
    style.type = StyleType.Polygon;
  } else if (qml.line) {
    style.type = StyleType.Line;
  } else if (qml.marker) {
    style.type = StyleType.Point;
  }

  // If we detected a type but have no visual properties, return null
  // so the caller will use default styles instead
  const hasVisualProps =
    style.color || style.fillColor || style.radius || style.weight;
  if (!hasVisualProps && style.type) {
    // Return just the type, but the caller should merge with defaults
    return { type: style.type };
  }

  return Object.keys(style).length > 0 ? style : null;
}

/**
 * Extracts style information from feature properties
 */
export function extractStyleFromProperties(
  properties: any,
): GeoPackageStyle | null {
  if (!properties) return null;

  // Look for common style properties
  const styleProps: string[] = [
    "style",
    "styles",
    "marker_style",
    "symbolizer",
    "color",
    "fill_color",
    "stroke_color",
    "radius",
    "width",
  ];

  // Check if there's a dedicated style property
  for (const prop of styleProps) {
    if (properties[prop] && typeof properties[prop] === "object") {
      return properties[prop];
    }
  }

  // Try to construct a style from individual properties
  const style: GeoPackageStyle = {};
  let hasStyle = false;

  // Handle common style properties that might be in the feature
  if (properties.color) {
    style.color = properties.color;
    hasStyle = true;
  }
  if (properties.fill_color || properties.fillColor) {
    style.fillColor = properties.fill_color || properties.fillColor;
    hasStyle = true;
  }
  if (properties.opacity) {
    style.opacity = Number(properties.opacity);
    hasStyle = true;
  }
  if (properties.fill_opacity || properties.fillOpacity) {
    style.fillOpacity = Number(
      properties.fill_opacity || properties.fillOpacity,
    );
    hasStyle = true;
  }
  if (properties.radius) {
    style.radius = Number(properties.radius);
    hasStyle = true;
  }
  if (properties.weight || properties.stroke_width) {
    style.weight = Number(properties.weight || properties.stroke_width);
    hasStyle = true;
  }
  if (properties.dash_array || properties.dashArray) {
    style.dashArray = properties.dash_array || properties.dashArray;
    hasStyle = true;
  }
  if (properties.icon) {
    style.icon = properties.icon;
    hasStyle = true;
  }

  return hasStyle ? style : null;
}

/**
 * Gets default style for a geometry type
 */
export function getDefaultStyle(geometryType: string): GeoPackageStyle {
  const lowerGeom = geometryType.toLowerCase();

  if (lowerGeom.includes("point")) {
    // Specific style for MultiPoint if needed
    if (lowerGeom.includes("multi")) {
      return {
        type: StyleType.MultiPoint,
        color: "#3388ff",
        fillColor: "#3388ff",
        weight: 1,
        radius: 6, // Smaller for multi-points
        opacity: 1,
        fillOpacity: 0.8,
      };
    }
    return {
      type: StyleType.Point,
      color: "#3388ff",
      fillColor: "#3388ff",
      weight: 1,
      radius: 8,
      opacity: 1,
      fillOpacity: 0.8,
    };
  } else if (lowerGeom.includes("line")) {
    // Specific style for MultiLine if needed
    if (lowerGeom.includes("multi")) {
      return {
        type: StyleType.MultiLine,
        color: "#3388ff",
        weight: 3,
        opacity: 0.8,
        dashArray: "5,5", // Add dash array for multi-lines
      };
    }
    return {
      type: StyleType.Line,
      color: "#3388ff",
      weight: 3,
      opacity: 0.8,
    };
  } else if (lowerGeom.includes("polygon")) {
    // Specific style for MultiPolygon if needed
    if (lowerGeom.includes("multi")) {
      return {
        type: StyleType.MultiPolygon,
        color: "#333333",
        fillColor: "#33AAFF",
        weight: 2,
        opacity: 1,
        fillOpacity: 0.5, // Slightly more transparent
      };
    }
    return {
      type: StyleType.Polygon,
      color: "#333333",
      fillColor: "#33AAFF",
      weight: 2,
      opacity: 1,
      fillOpacity: 0.6,
    };
  } else if (lowerGeom.includes("icon")) {
    return {
      type: StyleType.Icon,
      color: "#3388ff",
      fillColor: "#3388ff",
      radius: 12, // Default icon size
      opacity: 1,
    };
  }

  // Generic default
  return {
    color: "#3388ff",
    weight: 2,
    opacity: 0.8,
  };
}

/**
 * Converts GeoPackage style to Leaflet style
 */
export function toLeafletStyle(
  style: GeoPackageStyle,
  geometryType: string,
): L.PathOptions {
  const baseStyle = getDefaultStyle(geometryType);
  const mergedStyle = { ...baseStyle, ...style };

  // If it's an icon type, return a minimal set of options
  // since most style properties will be handled by createLeafletIcon
  if (
    mergedStyle.type === StyleType.Icon ||
    geometryType.toLowerCase().includes("icon")
  ) {
    return {
      opacity: mergedStyle.opacity || 1,
    };
  }

  // Convert to Leaflet PathOptions
  const leafletStyle: L.PathOptions = {
    color: mergedStyle.color,
    weight: mergedStyle.weight,
    opacity: mergedStyle.opacity,
    fillColor: mergedStyle.fillColor || mergedStyle.color,
    fillOpacity: mergedStyle.fillOpacity,
    dashArray: mergedStyle.dashArray,
  };

  return leafletStyle;
}

/**
 * Creates a Leaflet icon from a style
 */
export function createLeafletIcon(
  style: GeoPackageStyle,
): L.Icon | L.DivIcon | null {
  if (!style) return null;

  // Check if style includes an icon URL
  if (style.icon && typeof style.icon === "string") {
    return L.icon({
      iconUrl: style.icon,
      iconSize: [
        style.radius ? style.radius * 2 : 24,
        style.radius ? style.radius * 2 : 24,
      ],
      iconAnchor: [style.radius || 12, style.radius || 12],
    });
  }

  // Create a styled circle marker icon
  const size = style.radius || 8;
  const color = style.color || "#3388ff";
  const outlineColor = style.color || "#ffffff";
  const outlineWidth = style.weight || 1;

  return L.divIcon({
    html: `<div style="
      width: ${size * 2}px;
      height: ${size * 2}px;
      border-radius: 50%;
      background-color: ${color};
      border: ${outlineWidth}px solid ${outlineColor};
      opacity: ${style.opacity || 0.8};
    "></div>`,
    className: "custom-div-icon",
    iconSize: [size * 2, size * 2],
    iconAnchor: [size, size],
  });
}

/**
 * Applies style to a feature based on properties and layer style
 */
export function styleFeature(
  feature: GeoJSON.Feature,
  layerStyle: GeoPackageStyle | null,
  geometryType: string,
): L.PathOptions {
  // First check for style in feature properties
  const featureStyle = feature.properties
    ? extractStyleFromProperties(feature.properties)
    : null;

  // Determine the correct geometry type
  let actualGeometryType = geometryType;
  if (feature.geometry && feature.geometry.type) {
    actualGeometryType = feature.geometry.type;
  }

  // Merge styles with precedence: feature style > layer style > default style
  const mergedStyle = {
    ...getDefaultStyle(actualGeometryType),
    ...(layerStyle || {}),
    ...(featureStyle || {}),
  };

  return toLeafletStyle(mergedStyle, actualGeometryType);
}

/**
 * Create a layer popup with feature properties
 */
export function createPopupContent(properties: any, layerName: string): string {
  if (!properties) return "";

  // Add layer name as title
  let popupContent = `<h4>${layerName}</h4>`;

  // Filter out style properties
  const styleProps = [
    "style",
    "styles",
    "marker_style",
    "symbolizer",
    "color",
    "fill_color",
    "stroke_color",
    "radius",
    "width",
    "opacity",
    "fill_opacity",
  ];

  // Add important properties first (if they exist)
  const priorityProps = ["name", "id", "title", "description", "type"];
  for (const prop of priorityProps) {
    if (properties[prop]) {
      popupContent += `<strong>${prop}:</strong> ${properties[prop]}<br>`;
    }
  }

  // Add other properties (limit to a reasonable number)
  let count = 0;
  for (const [key, value] of Object.entries(properties)) {
    // Skip style properties and already added priority props
    if (styleProps.includes(key) || priorityProps.includes(key)) continue;

    // Skip complex objects and null values
    if (value === null || typeof value === "object") continue;

    // Limit to 10 properties for performance
    if (count >= 10) break;

    popupContent += `<strong>${key}:</strong> ${value}<br>`;
    count++;
  }

  return popupContent;
}
